import { DraggableProvided } from '@hello-pangea/dnd';
import {
	ListItem,
	ListItemText,
	Box,
	Divider,
	IconButton,
	InputAdornment,
	ButtonBase,
} from '@mui/material';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Icon } from '../../../../blocks/icons';
import styles from './InvoiceEditLineItem.module.scss';
import rpcShared from '@rockpapercoin/rpc-shared';
import MuiTextFieldText from '../../../../mui/MuiTextField/MuiTextFieldText/MuiTextFieldText';
import { Button } from '../../../Button';
import { useLineItems, LineItemDetails, OnUpdate } from '../../useLineItems';
import MuiCurrencyInput from '../../../../mui/MuiCurrencyInput';
import MuiNumberInput from '../../../../mui/MuiNumberInput';
import {
	InvoiceEditLineItemLineItemFragment,
	InvoiceEditLineItemsInvoiceFragment,
	InvoiceStatus,
} from '#types';
import { lineItemsAreMutable } from '../helpers';
import MuiTipTap, { MuiTipTapRef } from '../../../../mui/MuiTipTap';
import { DocumentNode, gql } from '@apollo/client';
import { useDeviceDetection } from '../../../../hooks/useDeviceDetection';

const invoiceEditLineItemLineItemFragment = gql`
	fragment invoiceEditLineItemLineItem on LineItem {
		id
		name
		rate
		quantity
		taxPercentage
		notes
	}
`;

const invoiceEditLineItemInvoiceFragment = gql`
	fragment invoiceEditLineItemInvoice on Invoice {
		id
		discountAmount
		discountPercentage
		lineItems {
			...invoiceEditLineItemLineItem
		}
	}
	${ invoiceEditLineItemLineItemFragment }
`;

const IfMutable: React.FC<{
	invoiceExists: boolean;
	invoice?: InvoiceEditLineItemsInvoiceFragment;
	className?: string;
	status?: InvoiceStatus;
	children: React.JSX.Element;
	otherwise?: React.JSX.Element | null;
}> = ( {
	invoiceExists,
	className,
	status,
	children,
	otherwise = <span className={ className }></span>,
} ) => ( lineItemsAreMutable( invoiceExists, status ) ? children : otherwise );

type LocalState = ReturnType<
	typeof rpcShared.invoices.calculateLineItemToString
> & { name: string };

export type InvoiceEditLineItemProps = {
	invoiceExists: boolean;
	invoice?: Omit<InvoiceEditLineItemsInvoiceFragment, 'id'> & { id?: string };
	lineItem: InvoiceEditLineItemLineItemFragment;
	lineItemDetails?: LineItemDetails;
	provided?: DraggableProvided;
	isDragging?: boolean;
	autoFocus: boolean;
	onUpdate: OnUpdate;
	onRemove: ReturnType<typeof useLineItems>['onRemoveLineItem'];
	onBlur?: () => void;
	rateLabel: string;
};

type InvoiceEditLineItemPropsWithFragment =
	React.FC<InvoiceEditLineItemProps> & {
		fragments: {
			invoiceEditLineItemInvoiceFragment: DocumentNode;
		};
	};

export const InvoiceEditLineItem: InvoiceEditLineItemPropsWithFragment = ( {
	invoiceExists,
	invoice,
	lineItem,
	lineItemDetails,
	provided,
	isDragging,
	autoFocus,
	onUpdate,
	onRemove,
	onBlur,
	rateLabel,
} ) => {
	const [ calculatedLineItem, setCalculatedLineItem ] = useState<LocalState>();
	const [ showNotes, setShowNotes ] = useState(
		lineItem.notes ? lineItem.notes.length > 0 : false
	);

	const backfillCalculatedLineItemValues = useCallback(
		( prevState?: Partial<LocalState> ) => ( {
			...prevState,
			name: prevState?.name || '',
			rate: prevState?.rate || '',
			quantity: prevState?.quantity || '',
			taxPercentage: prevState?.taxPercentage || '',
			lineTotal: prevState?.lineTotal || '',
			lineDiscount: prevState?.lineDiscount || '',
			lineTaxAmount: prevState?.lineTaxAmount || '',
		} ),
		[]
	);

	const isMutable = lineItemsAreMutable( invoiceExists, invoice?.status );
	const { isMobile } = useDeviceDetection();

	const notesRef = useRef<MuiTipTapRef>( null );
	const notesHasContent = !!lineItem.notes && lineItem.notes !== '<p></p>';
	useEffect( () => {
		if ( showNotes && !notesHasContent ) {
			notesRef.current?.focus();
		}
	}, [ notesHasContent, showNotes ] );

	useEffect( () => {
		const calculatedValue = rpcShared.invoices.calculateLineItemToString(
			invoice,
			lineItem,
			undefined,
			false
		);
		const backfilledValue = backfillCalculatedLineItemValues( {
			...calculatedValue,
			name: lineItem.name || '',
		} );
		setCalculatedLineItem( backfilledValue );
	}, [
		invoice,
		lineItem,
		backfillCalculatedLineItemValues
	] );

	if ( !calculatedLineItem ) {
		return null;
	}

	return (
		<>
			<ListItem
				disableGutters
				ref={ provided?.innerRef }
				className={ [
					styles.itemContainer,
					isDragging ? styles.isDragging : undefined,
					!isMutable ? styles.immutable : undefined,
					showNotes ? styles.showNotes : undefined,
				].join( ' ' ) }
				{ ...provided?.draggableProps }
				data-cy={ lineItem.id }
			>
				<IfMutable
					invoiceExists={ invoiceExists }
					status={ invoice?.status }
					className={ styles.dragHandleContainer }
					otherwise={ null }
				>
					<ButtonBase
						component='span'
						className={ styles.dragHandleContainer }
						{ ...provided?.dragHandleProps }
					>
						<Icon
							type='drag'
							className={ styles.icon }
							data-cy='line-item-edit-drag-button'
						/>
					</ButtonBase>
				</IfMutable>
				<MuiTextFieldText
					className={ styles.name }
					autoFocus={ autoFocus }
					label={ isMobile ? 'Name' : undefined }
					value={ calculatedLineItem?.name || '' }
					placeholder='Name'
					disabled={
						lineItemDetails?.disabled || lineItemDetails?.loading || false
					}
					errorText={ lineItemDetails?.validation[ 'name' ] }
					onChange={ ( e ) =>
						setCalculatedLineItem( ( prevState ) => ( {
							...prevState,
							...backfillCalculatedLineItemValues( prevState ),
							name: e.target.value,
						} ) )
					}
					onBlur={ ( e ) => {
						/* we're only updating the name if it's changed to save on re-renders to improve
						performance in instances where the list of line items is long */
						onUpdate( {
							name: typeof e.target.value === 'string' ? e.target.value : '',
						} );
						onBlur?.();
					} }
					slotProps={ {
						input: {
							endAdornment:
								lineItemsAreMutable( invoiceExists, invoice?.status ) &&
								!showNotes ? (
										<IconButton onClick={ () => setShowNotes( true ) }>
											<Icon
												type='editDetails'
												data-cy='line-item-edit-details-button'
												className={ styles.icon }
											/>
										</IconButton>
									) : undefined,
						},
						htmlInput: {
							'data-cy': 'name',
						},
					} }
				/>
				<MuiCurrencyInput
					className={ styles.rate }
					label={ isMobile ? rateLabel : undefined }
					value={
						typeof calculatedLineItem?.rate === 'string'
							? parseFloat( calculatedLineItem.rate.replace( ',', '' ) ) * 100
							: 0
					}
					blankWhenZero={ lineItem.rate === undefined ? true : undefined }
					placeholder='0.00'
					disabled={
						lineItemDetails?.disabled || lineItemDetails?.loading || false
					}
					errorText={ lineItemDetails?.validation[ 'rate' ] }
					slotProps={ {
						input: { startAdornment: '$' },
						htmlInput: {
							'data-cy': 'rate',
							inputMode: 'decimal',
							onBlur: ( e: { target: { value: string } } ) => {
								setCalculatedLineItem( ( prevState ) => ( {
									...prevState,
									...backfillCalculatedLineItemValues( prevState ),
									rate: e.target.value,
								} ) );
								onBlur?.();
							},
						},
					} }
					onChange={ ( rate ) => {
						onUpdate( { rate } );
						onBlur?.();
					} }
				/>
				<MuiNumberInput
					className={ styles.quantity }
					label={ isMobile ? 'Quantity' : undefined }
					value={
						typeof calculatedLineItem?.quantity === 'string'
							? parseFloat( calculatedLineItem.quantity )
							: 0
					}
					disabled={
						lineItemDetails?.disabled || lineItemDetails?.loading || false
					}
					errorText={ lineItemDetails?.validation[ 'quantity' ] }
					slotProps={ {
						htmlInput: {
							inputMode: 'decimal',
							'data-cy': 'quantity',
						},
					} }
					onChange={ ( quantity ) =>
						setCalculatedLineItem( ( prevState ) => ( {
							...prevState,
							...backfillCalculatedLineItemValues( prevState ),
							quantity: quantity.toString(),
						} ) )
					}
					onBlur={ ( quantity ) => {
						onUpdate( { quantity } );
						onBlur?.();
					} }
					min={ -10 }
					limit={ /-?^\d+(\.\d{0,2})?$/ }
				/>
				<MuiNumberInput
					className={ styles.taxPercentage }
					label={ isMobile ? 'Tax' : undefined }
					value={
						typeof calculatedLineItem?.taxPercentage === 'string'
							? parseFloat( calculatedLineItem.taxPercentage )
							: 0
					}
					disabled={
						lineItemDetails?.disabled || lineItemDetails?.loading || false
					}
					errorText={ lineItemDetails?.validation[ 'taxPercentage' ] }
					slotProps={ {
						input: {
							endAdornment: (
								<InputAdornment disableTypography position='start'>
									%
								</InputAdornment>
							),
						},
						htmlInput: {
							inputMode: 'decimal',
							'data-cy': 'tax',
						},
					} }
					onChange={ ( taxPercentage ) =>
						setCalculatedLineItem( ( prevState ) => ( {
							...prevState,
							...backfillCalculatedLineItemValues( prevState ),
							taxPercentage: taxPercentage.toString(),
						} ) )
					}
					onBlur={ ( taxPercentage ) => {
						onUpdate( { taxPercentage } );
						onBlur?.();
					} }
					max={ 100 }
					min={ 0 }
					limit={ /^\d{0,3}(\.\d{0,3})?$/ }
				/>
				<Box className={ styles.lineTotalContainer }>
					<ListItemText
						data-cy='line-item-total-mobile-label'
						className={ styles.lineTotalMobileLabel }
						disableTypography
					>
						Line total:
					</ListItemText>
					<ListItemText
						data-cy='line-item-total'
						className={ styles.lineTotalValue }
					>
						${ calculatedLineItem.lineTotal }
					</ListItemText>
				</Box>
				<IfMutable invoiceExists={ invoiceExists } status={ invoice?.status }>
					{ isMobile ? (
						<Button
							className={ styles.remove }
							onClick={ () => onRemove( lineItem.id ) }
							variant='text'
							color='secondary'
							startIcon={
								<Icon type='trash' data-cy='line-item-edit-trash-button' />
							}
						>
							Remove
						</Button>
					) : (
						<IconButton
							className={ styles.remove }
							onClick={ () => onRemove( lineItem.id ) }
							data-cy='invoice-edit-line-item-remove'
							aria-label='Remove line item'
						>
							<Icon
								type='trash'
								data-cy='line-item-edit-trash-button'
								className={ styles.icon }
							/>
						</IconButton>
					) }
				</IfMutable>
				{ showNotes ? (
					<MuiTipTap
						ref={ notesRef }
						className={ styles.notes }
						variant='outlined'
						label='Item details'
						countCharacters={ lineItemsAreMutable(
							invoiceExists,
							invoice?.status
						) }
						value={ lineItem.notes }
						onChange={ ( notes ) =>
							setCalculatedLineItem( ( prevState ) => ( {
								...prevState,
								...backfillCalculatedLineItemValues( prevState ),
								notes: notes === '<p></p>' ? null : notes,
							} ) )
						}
						disabled={
							!lineItemsAreMutable( invoiceExists, invoice?.status ) ||
							lineItemDetails?.loading
						}
						onBlur={ ( notes ) => {
							onUpdate( { notes: notes === '<p></p>' ? null : notes } );
							if ( notes === '<p></p>' ) {
								setShowNotes( false );
							}
						} }
					/>
				) : null }
			</ListItem>
			<Divider />
		</>
	);
};

InvoiceEditLineItem.fragments = {
	invoiceEditLineItemInvoiceFragment,
};
